import { Location } from '@angular/common';
import { Injectable, OnDestroy, signal } from '@angular/core';
import { environment } from 'environments/environment';
import { PrincipalService } from '../auth/principal.service';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RxStomp } from '@stomp/rx-stomp';
import { Buffer } from 'buffer';
import { map, Subscription } from 'rxjs';
import { SocketMessage } from '../model/socket-message.model';
import { EventManager } from './event-manager.service';
window.Buffer = window.Buffer || Buffer;

@Injectable({
    providedIn: 'root',
})
export class WebSocketService implements OnDestroy {
    userId: string;
    tenantId: string;
    public lastTime: number;
    subscriber: Subscription;
    private rxStomp?: RxStomp;
    private processesMessages: number[] = [];
    accessToken = signal<string>(null);
    userWatchSubscription: Subscription;
    tenantWatchSubscription: Subscription;
    everyoneWatchSubscription: Subscription;
    started = signal<boolean>(false);
    constructor(
        //private dialogService: DialogService,
        private eventManager: EventManager,
        private location: Location,
        private principal: PrincipalService
    ) {
        window.addEventListener('beforeunload', this.handleBeforeUnload);
    }

    setup(): void {
        console.log('WebSocketService initialized');
        this.rxStomp = new RxStomp();
        this.rxStomp.configure({
            // eslint-disable-next-line no-console
            debug: (msg: string): void => console.log(`[WebSocket Debug] ${new Date().toISOString()}:`, msg),
        });

        this.principal.state$.pipe(takeUntilDestroyed()).subscribe(result => {
            //debugger;
            if (result.authenticated && this.principal.accessToken && !this.stomp.active && !this.started()) {
                this.accessToken.set(this.principal.accessToken);
                this.userId = result.user.id;
                this.tenantId = result.user.tenantId;
                this.connect();
            }
            if (!result.authenticated && this.stomp.active) {
                this.disconnect();
                this.accessToken.set(null);
                this.userId = null;
                this.tenantId = null;
            }
        });

        this.subscriber = this.stomp.stompErrors$.subscribe(error => {
            console.error('STOMP error:', error);
            // Implement your error handling logic
        });

        this.rxStomp.connected$.subscribe(() => {
            this.clearSubscriptions();
            this.userWatchSubscription = this.stomp
                .watch(`/topic/${this.userId}`)
                .pipe(map(imessage => JSON.parse(imessage.body)))
                .subscribe({
                    next: (message: SocketMessage) => {
                        if (this.processesMessages.indexOf(message.id) == -1) {
                            this.processesMessages.push(message.id);
                            this.onMessageReceived(message);
                        }
                    },
                });

            this.tenantWatchSubscription = this.stomp
                .watch(`/topic/${this.tenantId}`)
                .pipe(map(imessage => JSON.parse(imessage.body)))
                .subscribe({
                    next: (message: SocketMessage) => {
                        if (this.processesMessages.indexOf(message.id) == -1) {
                            this.processesMessages.push(message.id);
                            this.onMessageReceived(message);
                        }
                    },
                });

            this.everyoneWatchSubscription = this.stomp
                .watch(`/topic/everyone`)
                .pipe(map(imessage => JSON.parse(imessage.body)))
                .subscribe({
                    next: (message: SocketMessage) => {
                        if (this.processesMessages.indexOf(message.id) == -1) {
                            this.processesMessages.push(message.id);
                            this.onMessageReceived(message);
                        }
                    },
                });
            //this.registerTopic(`/topic/${this.principal.authenticatedUser.id}`);
            console.log(`User Topic Created: /topic/${this.userId}`);
            console.log(`Tenant Topic Created: /topic/${this.tenantId}`);
            console.log(`Everyone Topic Created: /topic/everyone`);
        });
    }

    async reconnect(): Promise<void> {
        try {
            // First disconnect existing connection
            await this.disconnect();

            // Small delay to ensure clean disconnection
            await new Promise(resolve => setTimeout(resolve, 1000));

            // Update token and reconnect
            //this.accessToken.set(this.principal.accessToken);
            this.connect();
        } catch (error) {
            console.error('Error during WebSocket reconnection:', error);
            this.started.set(false);
        }
    }

    get stomp(): RxStomp {
        if (!this.rxStomp) {
            throw new Error('Stomp connection not initialized');
        }
        return this.rxStomp;
    }

    private connect(): void {
        this.started.set(true);

        this.updateCredentials();
        this.stomp.activate();
        // Add error handling
    }

    private disconnect() {
        this.started.set(false);
        this.processesMessages = [];

        // Cleanup subscriptions first
        this.clearSubscriptions();

        if (this.everyoneWatchSubscription) {
            this.everyoneWatchSubscription.unsubscribe();
            this.everyoneWatchSubscription = null;
        }

        // Force disconnect without sending DISCONNECT frame to avoid auth issues
        if (this.stomp.active) {
            return this.stomp
                .deactivate({ force: true })
                .then(() => {
                    console.log('Stomp disconnected');
                })
                .catch(error => {
                    // Ignore disconnect errors as they're often auth-related
                    console.warn('Stomp disconnect error (ignored):', error);
                });
        }
    }

    clearSubscriptions() {
        if (this.everyoneWatchSubscription) {
            this.everyoneWatchSubscription.unsubscribe();
            this.everyoneWatchSubscription = null;
        }
        if (this.subscriber) {
            this.subscriber.unsubscribe();
            this.subscriber = null;
        }
        if (this.userWatchSubscription) {
            this.userWatchSubscription.unsubscribe();
            this.userWatchSubscription = null;
        }
        if (this.tenantWatchSubscription) {
            this.tenantWatchSubscription.unsubscribe();
            this.tenantWatchSubscription = null;
        }
    }

    private buildUrl(): string {
        let url = '/web-socket/register';
        /*
        if (this.accessToken()) {
            url += `?access_token=${encodeURIComponent(this.accessToken())}`;
        }
            */
        url = this.location.prepareExternalUrl(url);
        url = url.replace('https://', 'wss://');
        url = url.replace('http://', 'ws://');
        return url;
    }

    private updateCredentials(): void {
        this.stomp.configure({
            //webSocketFactory: () => SockJS(this.buildUrl()),
            brokerURL: this.buildUrl(),
            //webSocketFactory: () => new WebSocket(this.buildUrl()),
            connectHeaders: {
                Authorization: `Bearer ${this.accessToken()}`,
                'ws-id': environment.wsId,
                login: 'guest',
                passcode: 'guest',
                authKey: environment.wsApiKey,
                'X-Platform': 'web',
                'X-Forwarded-Proto': environment.production ? 'https' : 'http',
            },
            //forceBinaryWSFrames: true,
            //appendMissingNULLonIncoming: true,
            heartbeatIncoming: 0, // Typical value 0 - disabled
            heartbeatOutgoing: 60000, // Typical value 20000 - every 20 seconds
            // Wait in milliseconds before attempting auto reconnect
            // Set to 0 to disable
            // Typical value 500 (500 milli seconds)
            reconnectDelay: 10000,
        });
    }

    onMessageReceived(message: SocketMessage): void {
        //console.log('RECEIVED-MESSAGE', message);
        try {
            console.log('Received Message:', message);
            if (message.event?.length > 0) {
                const events = message.event.split(',');
                events.forEach(event => {
                    setTimeout(
                        () => {
                            this.eventManager.broadcast({
                                name: event,
                                message: message,
                            });
                        },
                        message.waitTime ? message.waitTime : 0
                    );
                });
            }
        } catch (error) {
            console.error('Error processing socket message:', error);
        }
    }

    send(endpoint: string, data?: any) {
        this.stomp.publish({
            destination: endpoint,
            body: JSON.stringify(data),
        });
    }

    sendObject(endpoint: string, data?: any) {
        this.stomp.publish({
            destination: endpoint,
            body: data,
        });
    }

    kmsRegister() {
        /* if (this.socket && this.principal.isAuthenticated) {
            const user = this.principal.authenticatedUser;
            const data: any = {
                name: user.username,
                userId: user.id,
            };
            const jsonMessage = JSON.stringify(data);
            this.socket.send('/web-socket/kms-register', jsonMessage);
        } */
    }
    kmsUnRegister() {
        /* if (this.socket) {
            if (this.principal.isAuthenticated) {
                const user = this.principal.authenticatedUser;
                const data: any = {
                    name: user.username,
                    userId: user.id,
                };
                const jsonMessage = JSON.stringify(data);
                this.socket.send('/web-socket/kms-unregister', jsonMessage);
            }
        } */
    }

    kmsCall(to: string, sdpOffer: string) {
        /* if (this.socket) {
            const user = this.principal.authenticatedUser;
            const data: any = {
                userId: user.id,
                fullName: `${user.firstName} ${user.lastName}`,
                from: user.username,
                sdpOffer,
                to,
            };
            const jsonMessage = JSON.stringify(data);
            this.socket.send('/web-socket/kms-call', jsonMessage);
        } */
    }

    kmsIncomingCallResponse(sdpOffer: string, callResponse: string) {
        /* if (this.socket) {
            const user = this.principal.authenticatedUser;
            const data: any = {
                userId: user.id,
                from: user.username,
                fullName: `${user.firstName} ${user.lastName}`,
                sdpOffer,
                callResponse,
            };
            const jsonMessage = JSON.stringify(data);
            this.socket.send('/web-socket/kms-incomingCallResponse', jsonMessage);
        } */
    }

    kmsOnIceCandidate(candidate: string, sdpMid: string, sdpMLineIndex: string) {
        /* if (this.socket) {
            const user = this.principal.authenticatedUser;
            const data: any = {
                userId: user.id,
                candidate: {
                    candidate,
                    sdpMid,
                    sdpMLineIndex,
                },
            };
            const jsonMessage = JSON.stringify(data);
            this.socket.send('/web-socket/kms-onIceCandidate', jsonMessage);
        } */
    }

    kmsStop() {
        /*  if (this.socket) {
            const user = this.principal.authenticatedUser;
            const data: any = {
                userId: user.id,
            };
            const jsonMessage = JSON.stringify(data);
            this.socket.send('/web-socket/kms-stop', jsonMessage);
        } */
    }

    get isConnected() {
        return false;
        //return this.socket?.isConnected();
    }

    handleBeforeUnload = event => {
        this.disconnect();
    };

    ngOnDestroy(): void {
        window.removeEventListener('beforeunload', this.handleBeforeUnload.bind(this));
    }
}
